import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Stack, Button, Alert, Skeleton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, ToggleButton, ToggleButtonGroup, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import CampaignService from '../services/campaign.service';
import { Panel, PanelHead } from './dashboard/Surface';
import { DASH } from './dashboard/dashboardTokens';

/**
 * Kampane Effzone — prehľad mimo Clauda (super admin).
 *
 * Kampane vznikajú a odosielajú sa výhradne cez Effzone Analytics MCP; tu je
 * len to, čo treba vidieť a vedieť zastaviť: stav, čísla, výsledok, príjemcovia,
 * zrušenie jednej kampane a núdzová brzda pre všetky naraz.
 *
 * Brzda je jediná výrazná plocha stránky — keď je zatiahnutá, musí to byť
 * vidieť na prvý pohľad, inak sa človek čuduje, prečo nič nechodí.
 */

const STATUS_LABEL = {
    draft: 'Návrh',
    scheduled: 'Naplánovaná',
    sending: 'Odosiela sa',
    done: 'Hotová',
    cancelled: 'Zrušená',
    paused: 'Pozastavená',
};
const STATUS_COLOR = {
    draft: 'default',
    scheduled: 'info',
    sending: 'warning',
    done: 'success',
    cancelled: 'default',
    paused: 'warning',
};
const CHANNEL_LABEL = { in_app: 'Okno v appke', bot_chat: 'Správa od bota' };
const PLATFORM_LABEL = { mobile: 'Mobil', desktop: 'Desktop', web: 'Web' };
const GOAL_LABEL = {
    app_updated: 'Aktualizoval appku',
    profile_photo_added: 'Pridal fotku',
    position_filled: 'Vyplnil pozíciu',
    desktop_used: 'Použil desktop',
    task_created: 'Založil úlohu',
    meeting_created: 'Založil poradu',
    post_published: 'Pridal príspevok',
    attendance_clock_in: 'Označil príchod',
    message_sent: 'Poslal správu',
    mcp_used: 'Použil AI (MCP)',
    button_clicked: 'Klikol na tlačidlo',
    returned: 'Vrátil sa',
    active_7d: 'Aktívny 7. deň',
};
const SEND_STATUS_LABEL = {
    pending: 'Čaká',
    queued: 'Odložená (tichý čas)',
    sent: 'Odoslaná',
    failed: 'Zlyhala',
    holdout: 'Kontrolná skupina',
    suppressed_optout: 'Vypol si tipy',
    suppressed_company_optout: 'Firma vypla tipy',
    suppressed_push_disabled: 'Vypnuté notifikácie',
    suppressed_quiet: 'Tichý čas',
    suppressed_cap_day: 'Strop na deň',
    suppressed_cap_week: 'Strop na týždeň',
    suppressed_recent_same_goal: 'Rovnaký cieľ do 7 dní',
    suppressed_no_channel: 'Nedosiahnuteľný',
    suppressed_excluded: 'Vylúčený',
    suppressed_brake: 'Brzda',
    cancelled: 'Zrušená',
};
const FILTERS = [
    { id: '', label: 'Všetky' },
    { id: 'draft', label: 'Návrhy' },
    { id: 'scheduled', label: 'Naplánované' },
    { id: 'sending', label: 'Odosielajú sa' },
    { id: 'done', label: 'Hotové' },
    { id: 'cancelled', label: 'Zrušené' },
];

const fmtTime = (value) => (value ? dayjs(value).format('D. M. HH:mm') : '—');
// Server posiela percentá (rate_pct, napr. 27.3), nie zlomky.
const pct = (value) => (value === null || value === undefined || Number.isNaN(Number(value))
    ? '—'
    : `${Number(value).toLocaleString('sk-SK', { maximumFractionDigits: 1 })} %`);
const canCancel = (status) => ['draft', 'scheduled', 'sending', 'paused'].includes(status);
const errorText = (err, fallback) => err?.response?.data?.message || fallback;

const StatusChip = ({ status }) => (
    <Chip size="small" variant="outlined" color={STATUS_COLOR[status] || 'default'} label={STATUS_LABEL[status] || status} />
);

const BrakePanel = ({ enabled, busy, onToggle }) => (
    <Box
        sx={{
            borderRadius: `${DASH.radius}px`,
            p: DASH.pad,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            gap: 2,
            border: '1px solid',
            borderColor: enabled ? 'divider' : 'error.main',
            bgcolor: enabled ? 'background.paper' : 'error.main',
            color: enabled ? 'text.primary' : 'error.contrastText',
        }}
    >
        <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" gap={1.25}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: enabled ? 'success.main' : 'error.contrastText', flexShrink: 0 }} />
                <Typography sx={{ fontFamily: DASH.display, fontSize: 20, fontWeight: 600 }}>
                    {enabled ? 'Kampane bežia' : 'Núdzová brzda je zatiahnutá'}
                </Typography>
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.75, opacity: enabled ? 0.7 : 0.9, maxWidth: 640 }}>
                {enabled
                    ? 'Naplánované kampane sa odošlú v čase a čakajúce okná sa ľuďom ukážu.'
                    : 'Nič sa neodosiela a čakajúce okná sa nikomu neukážu — ani naplánované kampane. Platí, kým brzdu neuvoľníte.'}
            </Typography>
        </Box>
        <Button
            variant={enabled ? 'outlined' : 'contained'}
            color={enabled ? 'error' : 'inherit'}
            disableElevation
            disabled={busy || enabled === null}
            onClick={onToggle}
            sx={enabled ? undefined : { bgcolor: 'background.paper', color: 'text.primary', '&:hover': { bgcolor: 'background.default' } }}
        >
            {busy ? 'Ukladám…' : enabled ? 'Pozastaviť všetko' : 'Obnoviť kampane'}
        </Button>
    </Box>
);

// Dva riadky: hlavné číslo hore, zvyšok pod ním — v úzkom okne sa stĺpec
// nesmie natiahnuť cez okraj panelu.
const TwoLine = ({ main, sub, bold }) => (
    <Box sx={DASH.numeric}>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontWeight: bold ? 600 : 400 }}>{main}</Typography>
        {sub ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap' }}>{sub}</Typography> : null}
    </Box>
);

const sentLabel = (n) => (n === 1 ? '1 odoslaná' : n >= 2 && n <= 4 ? `${n} odoslané` : `${n} odoslaných`);

const Counts = ({ counts }) => (counts
    ? <TwoLine main={sentLabel(counts.sent ?? 0)} sub={`${counts.holdout ?? 0} kontrolná · ${counts.suppressed ?? 0} potlačených`} />
    : '—');

const Reactions = ({ counts }) => (counts
    ? <TwoLine main={`${counts.shown ?? 0} videlo`} sub={`${counts.clicked ?? 0} klik · ${counts.dismissed ?? 0} zavrelo`} />
    : '—');

// Cieľ a výsledok v jednej bunke: čo sa meria, a keď už je čo, koľko
// percent ho splnilo oproti kontrolnej skupine a predošlému obdobiu.
const GoalResult = ({ campaign }) => {
    if (!campaign.goal) return <Typography variant="body2" color="text.secondary">—</Typography>;
    const r = campaign.result;
    const goal = GOAL_LABEL[campaign.goal] || campaign.goal;
    return r
        ? <TwoLine main={`${goal} · ${pct(r.treatment_rate)}`} sub={`kontrolná ${pct(r.holdout_rate)} · predtým ${pct(r.prev_rate)}`} />
        : <TwoLine main={goal} sub={`do ${campaign.goal_window_days} dní`} />;
};

const DetailDialog = ({ campaignId, onClose, onCancelled }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [cancelInfo, setCancelInfo] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await CampaignService.detail(campaignId);
            setData(res.data);
            setError(null);
        } catch (err) {
            setError(errorText(err, 'Detail kampane sa nepodarilo načítať.'));
        }
    }, [campaignId]);

    useEffect(() => { setData(null); load(); }, [load]);

    const campaign = data?.campaign;
    const recipients = data?.recipients || [];
    const results = data?.results;

    const doCancel = async () => {
        setConfirmCancel(false);
        setCancelling(true);
        try {
            const res = await CampaignService.cancel(campaignId);
            setCancelInfo(`Kampaň je zrušená. Stiahnutých nezobrazených okien: ${res.data?.withdrawn ?? 0}.`);
            await load();
            onCancelled();
        } catch (err) {
            setError(errorText(err, 'Kampaň sa nepodarilo zrušiť.'));
        } finally {
            setCancelling(false);
        }
    };

    const goal = results?.goal;

    return (
        <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pr: 1.5 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontFamily: DASH.display, fontSize: 20, fontWeight: 600 }}>
                        {campaign ? campaign.name : 'Kampaň'}
                    </Typography>
                    {campaign && (
                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.75 }}>
                            <StatusChip status={campaign.status} />
                            <Typography variant="caption" color="text.secondary">
                                #{campaign.id} · {CHANNEL_LABEL[campaign.channel] || campaign.channel}
                                {campaign.channel === 'in_app' && Array.isArray(campaign.platforms)
                                    ? ` · ${campaign.platforms.map((p) => PLATFORM_LABEL[p] || p).join(', ')}` : ''}
                                {campaign.goal ? ` · cieľ: ${GOAL_LABEL[campaign.goal] || campaign.goal} do ${campaign.goal_window_days} dní` : ''}
                                {campaign.holdout_pct ? ` · kontrolná skupina ${campaign.holdout_pct} %` : ''}
                            </Typography>
                        </Stack>
                    )}
                </Box>
                <IconButton onClick={onClose} aria-label="Zavrieť" size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                {cancelInfo ? <Alert severity="success" sx={{ mb: 2 }}>{cancelInfo}</Alert> : null}
                {!data && !error ? (
                    <Stack gap={1}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={36} />)}</Stack>
                ) : null}
                {goal ? (
                    <Stack direction="row" gap={4} flexWrap="wrap" sx={{ mb: 3 }}>
                        {[['Dostali', goal.treatment], ['Kontrolná skupina', goal.holdout], ['Predošlé obdobie', goal.previous_period]].map(([label, g]) => (
                            <Box key={label}>
                                <Typography variant="caption" color="text.secondary">{label}</Typography>
                                <Typography sx={{ fontFamily: DASH.display, fontSize: 24, ...DASH.numeric }}>{pct(g?.rate_pct ?? g?.rate)}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={DASH.numeric}>
                                    {g ? `${g.converted ?? 0} z ${g.n ?? 0}` : '—'}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                ) : null}
                {results?.small_sample_note ? (
                    <Alert severity="info" sx={{ mb: 2 }}>{results.small_sample_note}</Alert>
                ) : null}
                {data ? (
                    recipients.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Kampaň zatiaľ nemá príjemcov.</Typography>
                    ) : (
                        <TableContainer sx={{ maxHeight: 520 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Človek</TableCell>
                                        <TableCell>Skupina</TableCell>
                                        <TableCell>Stav</TableCell>
                                        <TableCell>Odoslané</TableCell>
                                        <TableCell>Videl</TableCell>
                                        <TableCell>Klik</TableCell>
                                        <TableCell>Zavrel</TableCell>
                                        <TableCell>Cieľ</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {recipients.map((r) => (
                                        <TableRow key={r.employee_id} hover>
                                            <TableCell>
                                                <Typography variant="body2">{r.name || `#${r.employee_id}`}</Typography>
                                                <Typography variant="caption" color="text.secondary">{r.company || '—'}</Typography>
                                            </TableCell>
                                            <TableCell>{r.variant === 'holdout' ? 'Kontrolná' : 'Podnet'}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{SEND_STATUS_LABEL[r.send_status] || r.send_status}</Typography>
                                                {r.suppress_reason ? (
                                                    <Typography variant="caption" color="text.secondary">{r.suppress_reason}</Typography>
                                                ) : null}
                                            </TableCell>
                                            <TableCell sx={DASH.numeric}>{fmtTime(r.sent_at)}</TableCell>
                                            <TableCell sx={DASH.numeric}>
                                                {fmtTime(r.shown_at)}
                                                {r.shown_platform ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{r.shown_platform}</Typography> : null}
                                            </TableCell>
                                            <TableCell sx={DASH.numeric}>{fmtTime(r.clicked_at)}</TableCell>
                                            <TableCell sx={DASH.numeric}>{fmtTime(r.dismissed_at)}</TableCell>
                                            <TableCell>
                                                {r.converted === true || r.converted === 1
                                                    ? <Typography variant="body2" color="success.main">Splnil</Typography>
                                                    : r.converted === false || r.converted === 0
                                                        ? <Typography variant="body2" color="text.secondary">Nie</Typography>
                                                        : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )
                ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                {campaign && canCancel(campaign.status) ? (
                    <Button color="error" onClick={() => setConfirmCancel(true)} disabled={cancelling}>
                        {cancelling ? 'Ruším…' : 'Zrušiť kampaň'}
                    </Button>
                ) : null}
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose}>Zavrieť</Button>
            </DialogActions>

            <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Zrušiť kampaň?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Čo ešte nebolo odoslané, sa neodošle, a okná, ktoré ľudia ešte nevideli, sa stiahnu.
                        Zrušenú kampaň už nejde obnoviť.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmCancel(false)}>Späť</Button>
                    <Button color="error" variant="contained" disableElevation onClick={doCancel}>Áno, zrušiť</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

const Campaigns = () => {
    const [filter, setFilter] = useState('');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [brakeBusy, setBrakeBusy] = useState(false);
    const [confirmPause, setConfirmPause] = useState(false);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await CampaignService.list(filter);
            setData(res.data);
            setError(null);
        } catch (err) {
            setError(err?.response?.status === 404
                ? 'Server prehľad kampaní ešte nemá (GET /api/admin/campaigns).'
                : errorText(err, 'Kampane sa nepodarilo načítať.'));
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const enabled = data?.brake ? Boolean(Number(data.brake.campaigns_enabled)) : null;
    const campaigns = useMemo(() => data?.campaigns || [], [data]);

    const setBrake = async (pause) => {
        setConfirmPause(false);
        setBrakeBusy(true);
        try {
            const res = pause ? await CampaignService.pauseAll() : await CampaignService.resumeAll();
            setData((prev) => ({ ...(prev || {}), brake: { campaigns_enabled: res.data?.campaigns_enabled ?? (pause ? 0 : 1) } }));
            await load();
        } catch (err) {
            setError(errorText(err, 'Brzdu sa nepodarilo prepnúť.'));
        } finally {
            setBrakeBusy(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: DASH.gap }}>
            <Box>
                <Typography sx={{ fontFamily: DASH.display, fontSize: 26 }}>Kampane</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                    Tipy a novinky, ktoré Effzone posiela ľuďom naprieč firmami — okno v appke alebo správa od bota.
                    Vytvárajú sa a odosielajú cez Claude (Effzone Analytics MCP); tu ich vidíte a viete zastaviť.
                </Typography>
            </Box>

            {data || enabled !== null ? (
                <BrakePanel enabled={enabled} busy={brakeBusy} onToggle={() => (enabled ? setConfirmPause(true) : setBrake(false))} />
            ) : !error ? (
                <Skeleton variant="rounded" height={112} sx={{ borderRadius: `${DASH.radius}px` }} />
            ) : null}

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Panel padded={false} sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Box sx={{ p: DASH.pad, pb: 0 }}>
                    <PanelHead
                        title="Prehľad"
                        meta={data ? `${campaigns.length} ${campaigns.length === 1 ? 'kampaň' : campaigns.length >= 2 && campaigns.length <= 4 ? 'kampane' : 'kampaní'}` : null}
                        actions={(
                            <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_e, v) => { if (v !== null) setFilter(v); }}>
                                {FILTERS.map((f) => <ToggleButton key={f.id || 'all'} value={f.id} sx={{ textTransform: 'none', px: 1.5 }}>{f.label}</ToggleButton>)}
                            </ToggleButtonGroup>
                        )}
                    />
                </Box>
                {!data && !error ? (
                    <Stack gap={1} sx={{ p: DASH.pad, pt: 0 }}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={44} />)}</Stack>
                ) : campaigns.length === 0 ? (
                    <Box sx={{ p: DASH.pad, pt: 1 }}>
                        <Typography variant="body2">
                            {filter ? 'V tomto stave nie je žiadna kampaň.' : 'Zatiaľ tu nie je žiadna kampaň.'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Kampaň vznikne v Claude nástrojom preview_campaign ako návrh a objaví sa tu so stavom, počtami a neskôr aj výsledkom.
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 900 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ pl: 3 }}>Kampaň</TableCell>
                                    <TableCell>Stav</TableCell>
                                    <TableCell>Kanál</TableCell>
                                    <TableCell>Kedy</TableCell>
                                    <TableCell>Príjemcovia</TableCell>
                                    <TableCell>Reakcie</TableCell>
                                    <TableCell sx={{ pr: 3 }}>Cieľ a výsledok</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {campaigns.map((c) => (
                                    <TableRow key={c.id} hover onClick={() => setOpenId(c.id)} sx={{ cursor: 'pointer' }}>
                                        <TableCell sx={{ pl: 3, maxWidth: 280 }}>
                                            <Typography variant="body2" noWrap title={c.name}>{c.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                #{c.id}{c.parent_campaign_id ? ` · nadväzuje na #${c.parent_campaign_id}` : ''}{c.is_test ? ' · test' : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell><StatusChip status={c.status} /></TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{CHANNEL_LABEL[c.channel] || c.channel}</Typography>
                                            {c.channel === 'in_app' && Array.isArray(c.platforms) ? (
                                                <Typography variant="caption" color="text.secondary">{c.platforms.map((p) => PLATFORM_LABEL[p] || p).join(', ')}</Typography>
                                            ) : null}
                                        </TableCell>
                                        <TableCell sx={{ ...DASH.numeric, whiteSpace: 'nowrap' }}>
                                            {c.sent_at ? fmtTime(c.sent_at) : c.scheduled_at ? `plán ${fmtTime(c.scheduled_at)}` : '—'}
                                        </TableCell>
                                        <TableCell><Counts counts={c.counts} /></TableCell>
                                        <TableCell><Reactions counts={c.counts} /></TableCell>
                                        <TableCell sx={{ pr: 3 }}><GoalResult campaign={c} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Panel>

            {openId !== null ? (
                <DetailDialog campaignId={openId} onClose={() => setOpenId(null)} onCancelled={load} />
            ) : null}

            <Dialog open={confirmPause} onClose={() => setConfirmPause(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Pozastaviť všetky kampane?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Hneď sa prestane odosielať a čakajúce okná sa nikomu neukážu. Nič sa nezmaže —
                        po obnovení naplánované kampane pokračujú.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmPause(false)}>Späť</Button>
                    <Button color="error" variant="contained" disableElevation onClick={() => setBrake(true)}>Pozastaviť všetko</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Campaigns;
